// One header's wait in seconds, or null when it holds nothing usable (empty, malformed,
// negative, a date already past), so the caller falls through to the next header.
// Anything but a plain number is read as an HTTP date, and Date.parse reads "-5" as a
// day in 2001, which the sign check then turns away.
const headerSeconds = (raw) => {
  const value = String(raw ?? '').trim();
  const seconds = /^\d+(\.\d+)?$/.test(value)
    ? Number(value)
    : (Date.parse(value) - Date.now()) / 1000;
  return seconds >= 0 ? seconds : null;
};

/**
 * Seconds until the server's rate limit lets this client back in, or null when the
 * response does not say. express-rate-limit sends `RateLimit-Reset` (seconds) on every
 * response and `Retry-After` on a 429; Retry-After may also be an HTTP date. Cross-origin,
 * the browser hides both unless the API lists them in Access-Control-Expose-Headers, and
 * an unreadable header is simply absent here.
 *
 * @param {object} error A rejected axios request.
 * @returns {number|null}
 */
export const retryAfterSeconds = (error) => {
  const headers = error?.response?.headers || {};
  const seconds = headerSeconds(headers['ratelimit-reset']) ?? headerSeconds(headers['retry-after']);
  return seconds == null ? null : Math.max(1, Math.ceil(seconds));
};

// Past an hour the header is more likely misread (an epoch timestamp, say) than a real
// wait, and "16666667 minutes" helps nobody.
const MAX_WAIT_SECONDS = 60 * 60;

const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/**
 * "Please wait 2 minutes and try again." Whole minutes from one minute up, rounded up so
 * the wait it names is never too short, and no number at all when the server named none
 * or more than an hour.
 *
 * @param {number|null} seconds From retryAfterSeconds.
 * @returns {string}
 */
export const waitMessage = (seconds) => {
  if (seconds == null) return 'Please wait a moment and try again.';
  if (seconds > MAX_WAIT_SECONDS) return 'Please wait a while and try again.';
  const wait = seconds < 60 ? plural(seconds, 'second') : plural(Math.ceil(seconds / 60), 'minute');
  return `Please wait ${wait} and try again.`;
};
